// Internalized from @videoplatform/logging.
import moment = require('moment');

export enum LogLevel {
    Debug = 10,
    Info = 20,
    Warn = 30,
    Error = 40,
}

export interface ILogger {
    debug: (message: string) => void;
    info: (message: string) => void;
    warn: (message: string) => void;
    error: (message: string) => void;
}

export interface ILogMessage {
    loggername: string;
    level: LogLevel;
    timestamp: moment.Moment;
    stacktrace: string[];
    message: string;
}

export interface ILogHandler {
    handleLog: (logMesage: ILogMessage) => void;
}

export class DefaultLogger implements ILogger {
    constructor(private name: string, private messageHandler: ILogHandler) {}

    debug(message: string) {
        this.log(LogLevel.Debug, message);
    }

    error(message: string) {
        this.log(LogLevel.Error, message);
    }

    private getStackTrace(): string[] {
        let err = new Error();
        return err.stack ? err.stack.split('\n') : [];
    }

    info(message: string) {
        this.log(LogLevel.Info, message);
    }

    private log(level: LogLevel, message: string) {
        let logMessage: ILogMessage = {
            level,
            timestamp: moment(),
            stacktrace: this.getStackTrace(),
            message,
            loggername: this.name,
        };

        this.messageHandler.handleLog(logMessage);
    }

    warn(message: string) {
        this.log(LogLevel.Warn, message);
    }
}

export class ConsoleLogHandler implements ILogHandler {
    handleLog(logMessage: ILogMessage) {
        const fullMessage = `[${LogLevel[logMessage.level]}] ${logMessage.loggername}: ${logMessage.message}`;
        let truncatedMessage = fullMessage.substr(0, 100);
        if (fullMessage.length > 100) {
            truncatedMessage += '...';
        }

        // Don't log in production
        if (process.env.NODE_ENV == 'production') {
            return;
        }

        if (window.console) {
            if (logMessage.level == LogLevel.Warn && console.warn) {
                console.warn(truncatedMessage, logMessage);
            } else if (logMessage.level == LogLevel.Error && console.error) {
                console.error(truncatedMessage, logMessage);
            } else {
                console.log(truncatedMessage, logMessage);
            }
        }
    }
}

export function getLogger(name: string, handler?: ILogHandler) {
    return new DefaultLogger(name, handler || new ConsoleLogHandler());
}
